import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { layoutTitle } from "@/lib/tool-kit/charts/layout";
import { toRoman } from "@/lib/tool-kit/ecology/pedigree";
import {
  halfHeight,
  LABEL_FONT,
  LABEL_GAP,
  LINE_HEIGHT,
  type PedigreeLayout,
  type PlacedChild,
  type PlacedFamily,
  type PlacedPerson,
} from "@/lib/tool-kit/ecology/pedigree-layout";
import { CHART_INK } from "@/lib/tool-kit/palette";
import { type KeyLabels, keyHeight, PedigreeKey } from "./pedigree-key";
import {
  type GlyphLabels,
  PersonGlyph,
  type SymbolPaint,
} from "./pedigree-symbols";

const STROKE = 1.8;
const TITLE_SPACE = 6;

export interface PedigreeOptions {
  paint: SymbolPaint;
  showNumerals: boolean;
  showNumbers: boolean;
}

interface Segment {
  key: string;
  d: string;
  dashed?: boolean;
}

/** Consecutive children with the same twin mark form one set. */
function twinSets(children: readonly PlacedChild[]): PlacedChild[][] {
  const sets: PlacedChild[][] = [];
  for (const child of children) {
    const twin = child.placed.person.twin;
    const last = sets.at(-1);
    if (twin && last && last[0].placed.person.twin === twin) {
      last.push(child);
    } else {
      sets.push([child]);
    }
  }
  return sets;
}

/** Where a twin set's lines leave the sibship line; null for a lone child. */
function apexOf(set: readonly PlacedChild[]): number | null {
  if (set.length < 2 || !set[0].placed.person.twin) {
    return null;
  }
  return set.reduce((sum, c) => sum + c.placed.x, 0) / set.length;
}

function relationSegments(family: PlacedFamily, index: number): Segment[] {
  const { left, right, y, midX } = family;
  if (!left) {
    return [];
  }
  const start = left.x + halfHeight(left.person);
  const end = right ? right.x - halfHeight(right.person) : midX;
  const key = `rel${index}`;
  if (family.relation === "consanguineous") {
    return [-2.6, 2.6].map((offset) => ({
      key: `${key}${offset}`,
      d: `M${px(start)} ${px(y + offset)}H${px(end)}`,
    }));
  }
  const segments: Segment[] = [{ key, d: `M${px(start)} ${px(y)}H${px(end)}` }];
  if (family.relation === "separated" && right) {
    const at = midX - 14;
    for (const shift of [-3.5, 3.5]) {
      segments.push({
        key: `${key}s${shift}`,
        d: `M${px(at + shift - 4)} ${px(y + 9)}L${px(at + shift + 4)} ${px(y - 9)}`,
      });
    }
  }
  return segments;
}

function descentSegments(family: PlacedFamily, index: number): Segment[] {
  if (family.children.length === 0) {
    return [];
  }
  const { midX, y, sibshipY } = family;
  const drop = family.relation === "consanguineous" ? y + 2.6 : y;
  const sets = twinSets(family.children);
  const allX = [midX, ...sets.map((set) => apexOf(set) ?? set[0].placed.x)];
  const segments: Segment[] = [
    { key: `drop${index}`, d: `M${px(midX)} ${px(drop)}V${px(sibshipY)}` },
    {
      key: `sib${index}`,
      d: `M${px(Math.min(...allX))} ${px(sibshipY)}H${px(Math.max(...allX))}`,
    },
  ];
  for (const [setIndex, set] of sets.entries()) {
    const apex = apexOf(set);
    for (const child of set) {
      const from = apex ?? child.placed.x;
      segments.push({
        key: `kid${index}-${setIndex}-${child.placed.x}`,
        d: `M${px(from)} ${px(sibshipY)}L${px(child.placed.x)} ${px(child.topY)}`,
        dashed: child.placed.person.adopted,
      });
    }
    if (apex !== null && set[0].placed.person.twin === "mz") {
      const first = set[0];
      const last = set.at(-1) ?? first;
      const t = 0.45;
      const at = (child: PlacedChild, from: number) =>
        [
          from + (child.placed.x - from) * t,
          sibshipY + (child.topY - sibshipY) * t,
        ] as const;
      const [x1, y1] = at(first, apex);
      const [x2, y2] = at(last, apex);
      segments.push({
        key: `mz${index}-${setIndex}`,
        d: `M${px(x1)} ${px(y1)}L${px(x2)} ${px(y2)}`,
      });
    }
  }
  return segments;
}

function Labels({
  placed,
  showNumbers,
}: {
  placed: PlacedPerson;
  showNumbers: boolean;
}) {
  const lines = showNumbers ? [...placed.lines, placed.number] : placed.lines;
  const top = placed.y + halfHeight(placed.person) + LABEL_GAP;
  return (
    <g>
      {lines.map((line, index) => (
        <text
          fill={
            showNumbers && index === lines.length - 1
              ? CHART_INK.muted
              : CHART_INK.text
          }
          fontSize={LABEL_FONT}
          // Lines of one label: position is their identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional lines
          key={index}
          textAnchor="middle"
          x={px(placed.x)}
          y={px(top + index * LINE_HEIGHT + LABEL_FONT)}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

export function pedigreeSize(
  layout: PedigreeLayout,
  title: string,
  keyLabels: KeyLabels
): { width: number; height: number; keyTop: number; offset: number } {
  const width = layout.width;
  const heading = layoutTitle(title, width);
  const offset = heading ? heading.height + TITLE_SPACE : 0;
  const keyTop = layout.height + offset;
  return {
    width,
    height: Math.ceil(keyTop + keyHeight(layout.used, width, keyLabels)),
    keyTop,
    offset,
  };
}

export function PedigreeDiagram({
  id,
  layout,
  options,
  title,
  keyLabels,
  glyphLabels,
  label,
  description,
}: {
  id: string;
  layout: PedigreeLayout;
  options: PedigreeOptions;
  title: string;
  keyLabels: KeyLabels;
  glyphLabels: GlyphLabels;
  label: string;
  description: string;
}) {
  const { width, height, keyTop, offset } = pedigreeSize(
    layout,
    title,
    keyLabels
  );
  const heading = layoutTitle(title, width);
  const segments = layout.families.flatMap((family, index) => [
    ...relationSegments(family, index),
    ...descentSegments(family, index),
  ]);
  return (
    <ChartSvg
      description={description}
      height={height}
      id={id}
      label={label}
      width={width}
    >
      <ChartTitle title={heading} />
      <g transform={`translate(0 ${offset})`}>
        <g
          fill="none"
          stroke={CHART_INK.text}
          strokeLinecap="square"
          strokeWidth={STROKE}
        >
          {segments.map((segment) => (
            <path
              d={segment.d}
              key={segment.key}
              strokeDasharray={segment.dashed ? "6 4" : undefined}
            />
          ))}
        </g>
        {options.showNumerals
          ? layout.rows.map((row, index) => (
              <text
                fill={CHART_INK.text}
                fontSize={14}
                fontWeight="bold"
                key={`gen${row}`}
                x={20}
                y={px(row + 5)}
              >
                {toRoman(index + 1)}
              </text>
            ))
          : null}
        {layout.people.map((placed) => (
          <g key={`${placed.number}-${placed.x}`}>
            <PersonGlyph
              labels={glyphLabels}
              paint={options.paint}
              person={placed.person}
              x={placed.x}
              y={placed.y}
            />
            <Labels placed={placed} showNumbers={options.showNumbers} />
          </g>
        ))}
      </g>
      <g transform={`translate(0 ${keyTop})`}>
        <PedigreeKey
          labels={keyLabels}
          paint={options.paint}
          used={layout.used}
          width={width}
        />
      </g>
    </ChartSvg>
  );
}
